import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  Shield, 
  MapPin, 
  CheckCircle2,
  Award,
  Calendar,
  Dumbbell,
  Bike,
  Mountain,
  Fish,
  Palette,
  Gamepad2,
  Heart,
  Wrench,
  Target,
  Timer,
  ShieldCheck
} from 'lucide-react';

/**
 * SportsServiceCard - Individual service cards based on wireframe specifications
 * Each service gets its own dedicated card with specific features
 */

// Service-specific configurations for Sports Equipment
const SPORTS_EQUIPMENT_SERVICES = {
  'goal-post-installation': {
    icon: '⚽',
    name: 'Goal Post Installation',
    subtitle: 'Football · Garden · School',
    features: ['Secure ground anchoring', 'Safety checked'],
    badges: ['Insured installer'],
    ctaText: 'Book installation',
    type: 'setup_installation',
  },
  'sports-equipment-assembly': {
    icon: '🧰',
    name: 'Sports Equipment Assembly',
    subtitle: 'Goals · Hoops · Net Systems',
    features: ['Professional assembly', 'Tools included'],
    badges: [],
    ctaText: 'Book assembly',
    type: 'setup_installation',
  },
  'net-installation': {
    icon: '🥅',
    name: 'Net Installation & Replacement',
    subtitle: 'Tennis · Badminton · Volleyball',
    features: ['Precision fitting', 'All net types'],
    badges: ['Verified installer'],
    ctaText: 'Book installation',
    type: 'setup_installation',
  },
  'basketball-hoop-installation': {
    icon: '🏀',
    name: 'Basketball Hoop Installation',
    subtitle: 'Wall-mount · In-ground · Portable',
    features: ['Professional mounting', 'Height adjustment'],
    badges: ['Safety certified'],
    ctaText: 'Book installation',
    type: 'setup_installation',
  },
  'safety-check': {
    icon: '🛡️',
    name: 'Sports Equipment Safety Check',
    subtitle: 'Anchoring · Stability · Weatherproof',
    features: ['Ideal after DIY installs', 'Peace of mind'],
    badges: [],
    ctaText: 'Book safety check',
    type: 'consultation',
  },
  'ground-anchoring': {
    icon: '⚓',
    name: 'Ground Anchoring',
    subtitle: 'Secure mounting · Wind resistance',
    features: ['Heavy-duty anchors', 'All soil types'],
    badges: [],
    ctaText: 'Book anchoring',
    type: 'setup_installation',
  },
  'relocation': {
    icon: '🔄',
    name: 'Relocation / Re-installation',
    subtitle: 'Move & reinstall equipment',
    features: ['Careful disassembly', 'New location setup'],
    badges: [],
    ctaText: 'Book relocation',
    type: 'setup_installation',
  },
  'repair-maintenance': {
    icon: '🔧',
    name: 'Repair & Maintenance',
    subtitle: 'Fixes · Parts · Servicing',
    features: ['Quick turnaround', 'Genuine parts'],
    badges: [],
    ctaText: 'Book repair',
    type: 'repair_maintenance',
  },
  'playground-setup': {
    icon: '🎢',
    name: 'Playground Setup',
    subtitle: 'Full playground installation',
    features: ['Multi-equipment setup', 'Safety compliant'],
    badges: ['Insured', 'Licensed'],
    ctaText: 'Get quote',
    type: 'setup_installation',
  },
  'line-marking': {
    icon: '📏',
    name: 'Line Marking',
    subtitle: 'Court & field markings',
    features: ['Professional grade paint', 'Regulation specs'],
    badges: [],
    ctaText: 'Book marking',
    type: 'mobile_service',
  },
  // 🏆 COACHING SERVICES
  'individual-sports-coaching': {
    icon: '🏆',
    name: 'Individual Sports Coaching',
    subtitle: '1-on-1 training · All sports',
    features: ['Personalized training', 'Technique focus'],
    badges: ['Certified coach'],
    ctaText: 'Book session',
    type: 'coaching_individual',
    isCoaching: true,
  },
  'group-sports-training': {
    icon: '👥',
    name: 'Group Sports Training',
    subtitle: 'Team sessions · 2-10 people',
    features: ['Team building', 'Cost effective'],
    badges: [],
    ctaText: 'Book group',
    type: 'coaching_group',
    isCoaching: true,
  },
  'online-sports-coaching': {
    icon: '💻',
    name: 'Online Sports Coaching',
    subtitle: 'Video sessions · Technique analysis',
    features: ['Flexible timing', 'Video review'],
    badges: [],
    ctaText: 'Book online',
    type: 'coaching_online',
    isCoaching: true,
  },
  'sports-camp---clinic': {
    icon: '🏕️',
    name: 'Sports Camp / Clinic',
    subtitle: 'Multi-day training programs',
    features: ['Intensive training', 'Skill development'],
    badges: ['Professional coaches'],
    ctaText: 'Join camp',
    type: 'coaching_camp',
    isCoaching: true,
  },
};

// Fitness & Gym service configurations
const FITNESS_GYM_SERVICES = {
  'personal-training': {
    icon: '🏋️',
    name: 'Personal Training Session',
    subtitle: '1-on-1 coaching',
    features: ['Personalized workout', 'Form correction'],
    badges: ['Certified trainer'],
    ctaText: 'Book session',
    type: 'coaching_pt',
  },
  'fitness-assessment': {
    icon: '📊',
    name: 'Fitness Assessment',
    subtitle: 'Body composition · Goals setting',
    features: ['Full body analysis', 'Custom plan'],
    badges: [],
    ctaText: 'Book assessment',
    type: 'consultation',
  },
  'custom-workout-plan': {
    icon: '📋',
    name: 'Custom Workout Plan',
    subtitle: 'Personalized training program',
    features: ['Goal-focused', 'Progressive overload'],
    badges: [],
    ctaText: 'Get plan',
    type: 'coaching_pt',
  },
  'online-coaching': {
    icon: '💻',
    name: 'Online Coaching',
    subtitle: 'Virtual training sessions',
    features: ['Flexible scheduling', 'Video calls'],
    badges: [],
    ctaText: 'Book online',
    type: 'coaching_pt',
  },
  'mobility-stretching': {
    icon: '🧘',
    name: 'Mobility & Stretching',
    subtitle: 'Flexibility · Recovery',
    features: ['Injury prevention', 'Better range'],
    badges: [],
    ctaText: 'Book class',
    type: 'lesson_class',
  },
  'weight-loss-program': {
    icon: '⚖️',
    name: 'Weight Loss Program',
    subtitle: 'Structured fat loss plan',
    features: ['Nutrition guidance', 'Weekly check-ins'],
    badges: ['Results guaranteed'],
    ctaText: 'Start program',
    type: 'coaching_pt',
  },
  'strength-conditioning': {
    icon: '💪',
    name: 'Strength & Conditioning',
    subtitle: 'Build muscle · Athletic performance',
    features: ['Progressive training', 'Performance tracking'],
    badges: [],
    ctaText: 'Start training',
    type: 'coaching_pt',
  },
  'nutrition-guidance': {
    icon: '🥗',
    name: 'Nutrition Guidance',
    subtitle: 'Diet planning · Meal prep',
    features: ['Macro planning', 'Recipe ideas'],
    badges: ['Certified nutritionist'],
    ctaText: 'Get guidance',
    type: 'consultation',
  },
  'post-rehab-training': {
    icon: '🩹',
    name: 'Post-Rehab Training',
    subtitle: 'Recovery · Injury prevention',
    features: ['Gentle progression', 'Medical clearance'],
    badges: ['Rehab certified'],
    ctaText: 'Book session',
    type: 'coaching_pt',
  },
  'small-group-training': {
    icon: '👥',
    name: 'Small Group Training',
    subtitle: '2-4 people · Semi-private',
    features: ['Social motivation', 'Cost effective'],
    badges: [],
    ctaText: 'Join group',
    type: 'coaching_group',
    isCoaching: true,
  },
  'group-fitness-class': {
    icon: '🏃',
    name: 'Group Fitness Class',
    subtitle: 'HIIT · Bootcamp · Circuit',
    features: ['High energy', 'Community'],
    badges: [],
    ctaText: 'Join class',
    type: 'coaching_group',
    isCoaching: true,
  },
  'fitness-bootcamp': {
    icon: '💪',
    name: 'Fitness Bootcamp',
    subtitle: 'Intensive multi-week program',
    features: ['Transformational', 'Full support'],
    badges: ['Results guaranteed'],
    ctaText: 'Join bootcamp',
    type: 'coaching_camp',
    isCoaching: true,
  },
  'home-gym-setup': {
    icon: '🏠',
    name: 'Home Gym Assembly & Setup',
    subtitle: 'Treadmills · Racks · Benches',
    features: ['Expert assembly', 'Safety check'],
    badges: ['Insured'],
    ctaText: 'Book setup',
    type: 'setup_installation',
  },
};

// Get service config by subcategory
const getServiceConfigs = (subcategory) => {
  switch (subcategory) {
    case 'sports-equipment':
      return SPORTS_EQUIPMENT_SERVICES;
    case 'fitness-gym':
      return FITNESS_GYM_SERVICES;
    default:
      return {};
  }
};

// Primary Service Card (Individual service)
export const ServiceCard = ({
  service,
  provider,
  subcategory = 'sports-equipment',
  onBook,
  isSponsored = false,
  placement = 'inline', // 'top', 'inline', 'post-checkout', 'post-delivery'
}) => {
  // Get service-specific config
  const serviceConfigs = getServiceConfigs(subcategory);
  const serviceKey = service.name?.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
  const config = serviceConfigs[serviceKey] || {
    icon: service.is_coaching ? '🏆' : '🔧',
    name: service.name,
    subtitle: service.description || service.duration,
    features: [],
    badges: [],
    ctaText: service.is_coaching ? 'Book session' : 'Book now',
    type: service.type,
    isCoaching: service.is_coaching,
  };

  const tierColors = {
    core: 'border-green-200 bg-green-50/30',
    premium: 'border-amber-200 bg-amber-50/30',
    conditional: 'border-orange-200 bg-orange-50/30',
  };

  // Check if this is a coaching service
  const isCoaching = service.is_coaching || config.isCoaching;
  const serviceLabel = isCoaching 
    ? '🏆 Sports Coaching' 
    : (subcategory === 'sports-equipment' ? 'Sports Equipment Service' : 'Fitness Service');

  return (
    <Card 
      className={`overflow-hidden border-2 hover:shadow-lg transition-all ${tierColors[service.tier] || 'border-gray-200'}`}
      data-testid={`service-card-${serviceKey}`}
    >
      <CardContent className="p-4">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · {serviceLabel}</span>
          </div>
        )}

        {/* Coaching Badge (if not sponsored) */}
        {!isSponsored && isCoaching && (
          <div className="flex items-center gap-1 text-purple-600 text-xs mb-2">
            <span>🏆</span>
            <span>Professional Coaching</span>
          </div>
        )}

        {/* Service Name & Subtitle */}
        <div className="mb-3">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <span className="text-xl">{config.icon}</span>
            {config.name}
          </h3>
          <p className="text-sm text-gray-500">{config.subtitle}</p>
        </div>

        {/* Sports offered (for coaching) */}
        {isCoaching && service.sports && service.sports.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {service.sports.slice(0, 4).map((sport, idx) => (
              <Badge key={idx} variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                {sport}
              </Badge>
            ))}
            {service.sports.length > 4 && (
              <Badge variant="outline" className="text-xs bg-gray-50 text-gray-600">
                +{service.sports.length - 4} more
              </Badge>
            )}
          </div>
        )}

        {/* Session type for group coaching */}
        {isCoaching && service.max_participants && service.max_participants > 1 && (
          <p className="text-xs text-purple-600 mb-2">
            👥 Up to {service.max_participants} participants
          </p>
        )}

        {/* Features */}
        <div className="space-y-1 mb-3">
          {config.features.map((feature, idx) => (
            <p key={idx} className="text-sm text-green-700 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              {feature}
            </p>
          ))}
        </div>

        {/* Badges */}
        {config.badges.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {config.badges.map((badge, idx) => (
              <Badge key={idx} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                <ShieldCheck className="h-3 w-3 mr-1" />
                {badge}
              </Badge>
            ))}
          </div>
        )}

        {/* Rating & Location */}
        <div className="flex items-center justify-between text-sm mb-3">
          <div className="flex items-center gap-1">
            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.8'}</span>
            <span className="text-gray-500">({provider?.review_count || 0} {service.type === 'setup_installation' ? 'installs' : 'sessions'})</span>
          </div>
          {provider?.city && (
            <span className="text-gray-500 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {provider.travel_radius_km ? `${provider.travel_radius_km} km` : provider.city}
            </span>
          )}
        </div>

        {/* Price */}
        <p className="text-sm mb-4">
          From <span className="font-bold text-lg">{provider?.currency || 'Rs'} {service.price}</span>
        </p>

        {/* CTA Button */}
        <Button 
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          onClick={() => onBook?.(service, provider)}
          data-testid={`book-btn-${serviceKey}`}
        >
          {config.ctaText}
        </Button>
      </CardContent>
    </Card>
  );
};

// Dual Service Card (Two services side by side - best monetization slot)
export const DualServiceCard = ({
  services,
  providers,
  subcategory = 'sports-equipment',
  onViewAll,
}) => {
  if (!services || services.length < 2) return null;
  
  const serviceConfigs = getServiceConfigs(subcategory);

  return (
    <Card className="overflow-hidden border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white" data-testid="dual-service-card">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center gap-1 text-blue-600 text-xs mb-4">
          <span>🔹</span>
          <span>Sponsored · Setup & Safety Services</span>
        </div>

        {/* Two services side by side */}
        <div className="grid grid-cols-2 gap-4">
          {services.slice(0, 2).map((service, idx) => {
            const provider = providers?.[idx] || providers?.[0];
            const serviceKey = service.name?.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
            const config = serviceConfigs[serviceKey] || { icon: '🔧', name: service.name };
            
            return (
              <div key={idx} className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{config.icon}</span>
                  <span className="font-medium text-sm">{config.name?.split(' ').slice(0, 2).join(' ')}</span>
                </div>
                <p className="text-sm text-gray-600">
                  From {provider?.currency || 'Rs'} {service.price}
                </p>
                <div className="flex items-center gap-1 text-xs">
                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                  <span>{provider?.rating?.toFixed(1) || '4.8'} ({provider?.review_count || 0})</span>
                </div>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Timer className="h-3 w-3" />
                  {service.availability || 'Today · Available'}
                </p>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <Button 
          variant="outline" 
          className="w-full mt-4 border-blue-300 text-blue-700 hover:bg-blue-50"
          onClick={onViewAll}
        >
          View services near you
        </Button>
      </CardContent>
    </Card>
  );
};

// Safety/Post-DIY Card (shown after delivery or scroll)
export const SafetyServiceCard = ({
  service,
  provider,
  trigger = 'post-delivery', // 'post-delivery', 'post-diy', 'scroll'
  onBook,
}) => {
  return (
    <Card className="overflow-hidden border-2 border-green-200 bg-gradient-to-br from-green-50 to-white" data-testid="safety-service-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-1 text-green-600 text-xs mb-2">
          <span>🔹</span>
          <span>Sponsored · Safety Service</span>
        </div>

        <div className="mb-3">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <span className="text-xl">🛡️</span>
            Sports Equipment Safety Check
          </h3>
          <p className="text-sm text-gray-500">Anchoring · Stability · Weatherproof</p>
        </div>

        <div className="space-y-1 mb-3">
          <p className="text-sm text-green-700 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            Ideal after DIY installs
          </p>
          <p className="text-sm text-green-700 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            Peace of mind
          </p>
        </div>

        <div className="flex items-center gap-2 text-sm mb-3">
          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
          <span className="text-gray-500">({provider?.review_count || 210} checks)</span>
        </div>

        <p className="text-sm mb-4">
          From <span className="font-bold text-lg">{provider?.currency || 'Rs'} {service?.price || 2500}</span>
        </p>

        <Button 
          className="w-full bg-green-600 hover:bg-green-700 text-white"
          onClick={() => onBook?.(service, provider)}
        >
          Book safety check
        </Button>
      </CardContent>
    </Card>
  );
};

// Mobile Sticky Pill
export const MobileStickyPill = ({
  subcategory = 'sports-equipment',
  startingPrice = 45,
  currency = '£',
  onTap,
}) => {
  const configs = {
    'sports-equipment': { icon: '⚽', text: 'Need installation help?' },
    'fitness-gym': { icon: '🏋️', text: 'Need a trainer?' },
    'outdoor-camping': { icon: '🏕️', text: 'Need a guide?' },
    'cycling-equipment': { icon: '🚴', text: 'Need bike service?' },
  };
  
  const config = configs[subcategory] || configs['sports-equipment'];

  return (
    <div 
      className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 lg:hidden"
      data-testid="mobile-sticky-pill"
    >
      <button
        onClick={onTap}
        className="flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all"
      >
        <span>{config.icon}</span>
        <span className="text-sm font-medium">{config.text}</span>
        <span className="text-sm">From {currency}{startingPrice}</span>
      </button>
    </div>
  );
};

// Mobile Bottom Sheet Content
export const MobileServicesSheet = ({
  services,
  providers,
  subcategory,
  onBook,
}) => {
  const serviceConfigs = getServiceConfigs(subcategory);
  
  const categoryTitles = {
    'sports-equipment': 'Sports Equipment Services Near You',
    'fitness-gym': 'Fitness Services Near You',
  };

  return (
    <div className="p-4 space-y-4">
      <h3 className="font-bold text-lg">{categoryTitles[subcategory] || 'Services Near You'}</h3>
      
      <div className="space-y-3">
        {services?.slice(0, 5).map((service, idx) => {
          const provider = providers?.[0];
          const serviceKey = service.name?.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
          const config = serviceConfigs[serviceKey] || { icon: '🔧', name: service.name };
          
          return (
            <div 
              key={idx}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{config.icon}</span>
                <div>
                  <p className="font-medium text-sm">{config.name}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-2">
                    <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                    {provider?.rating?.toFixed(1) || '4.8'} · {provider?.city || '2.3 km'} · From {provider?.currency || 'Rs'} {service.price}
                  </p>
                </div>
              </div>
              <Button 
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => onBook?.(service, provider)}
              >
                Book
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Export all components
export {
  SPORTS_EQUIPMENT_SERVICES,
  FITNESS_GYM_SERVICES,
  getServiceConfigs,
};

export default ServiceCard;
